import * as React from 'react';
import type { CircleLoaderColor } from '../CircleLoader/CircleLoader';
import CircleLoader from '../CircleLoader/CircleLoader';
import type { Props as HybridLinkProps } from '../HybridLink/HybridLink';
import HybridLink from '../HybridLink/HybridLink';
import type { DataAttributes } from '../../../modules/react-utils';
import cn from '~modules/cn';
import './button.scss';

export type ButtonType =
    | 'primary'
    | 'secondary'
    | 'secondary-core'
    | 'tertiary'
    | 'positive'
    | 'negative'
    | 'transparent'
    | 'transparent-dark'
    | 'upgrade'
    | 'positive-transparent'
    | 'negative-transparent';
export type ButtonWidth = 'full' | 'full-mobile' | 'auto' | 'fit';

export type ButtonSize = 'large' | 'medium' | 'small' | 'none';

export type ButtonProps = {
    buttonType: ButtonType;
    buttonSize?: ButtonSize;
    buttonWidth?: ButtonWidth;
    iconPosition?: 'left' | 'right';
    icon?: JSX.Element;
    loading?: boolean;
    active?: boolean;
};

export type InheritedProps = React.ComponentPropsWithoutRef<'button'> & DataAttributes;

export type ButtonFullProps = InheritedProps & ButtonProps;

/*
 *  We need to define the defaultProps as an exportable object to reuse it in Storybook
 *  Storybook is supposed to infer default values from Typescript and or function({props}) but it is really buggy and never works 100%.
 */
export const defaultProps: ButtonFullProps = {
    buttonType: 'secondary',
    buttonSize: 'medium',
    buttonWidth: 'auto',
    iconPosition: 'left',
    disabled: false,
    loading: false,
    active: false
};

const LoaderColor = {
    positive: 'positive',
    secondary: 'dark',
    'secondary-core': 'dark',
    tertiary: 'dark',
    transparent: 'dark',
    'positive-transparent': 'positive',
    'negative-transparent': 'dark'
} as Record<ButtonType, CircleLoaderColor>;

const mergeProps = (props: ButtonFullProps): ButtonFullProps => ({
    ...defaultProps,
    ...props
});

export const getClassNameFromProps = (
    props: ButtonProps & { children?: React.ReactNode; className?: string },
    options?: { [key: string]: string | boolean }
) => {
    const { className, buttonType, buttonSize, buttonWidth, icon, loading, children, active } = mergeProps(props);

    return cn(className, 'button', `button--type-${buttonType}`, {
        [`button--size-${buttonSize}`]: buttonSize,
        [`button--width-${buttonWidth}`]: buttonWidth,
        'button--icon-only': icon && !loading && !children,
        'button--loading': loading,
        'button--active': active
    });
};

const contentClassName = 'button__content';

const ButtonContent = (props: ButtonProps & { children?: React.ReactNode }) => {
    const { buttonType, loading, iconPosition, icon, children } = mergeProps(props);

    return (
        <>
            {iconPosition === 'left' && !loading && icon}
            {loading && <CircleLoader type="sharp" subtype="full" color={LoaderColor[buttonType] || 'light'} className="button-loader" />}
            {children && <span className={contentClassName}>{children}</span>}
            {iconPosition === 'right' && !loading && icon}
        </>
    );
};

const getTipOverflow = (props: ButtonFullProps) => {
    if (props['data-tip-overflow'] === true) {
        // show data-tip when text contents is overflowed
        return contentClassName;
    }
    return undefined;
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonFullProps>((props, ref): JSX.Element => {
    // We destructure to prevent passing Button props that aren't used for the button DOM element
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { className, buttonType, buttonSize, buttonWidth, loading, iconPosition, icon, active, ...rest } = mergeProps(props);
    const tipOverflow = getTipOverflow(props);

    return (
        <button className={getClassNameFromProps(props)} ref={ref} {...rest} data-tip-overflow={tipOverflow}>
            <ButtonContent {...props} />
        </button>
    );
});

export type HybridLinkButtonProps = Omit<ButtonFullProps, 'onClick'> & HybridLinkProps;

/**
 * A {@link HybridLink} that renders with the styles of a {@link Button}.
 *
 * Prefer to use this component if you need a link that looks like a button.
 */
export const HybridLinkButton = React.forwardRef<HTMLAnchorElement, HybridLinkButtonProps>((props, ref): JSX.Element => {
    // We destructure to calculate linkProps
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { className, buttonType, buttonSize, buttonWidth, loading, disabled, iconPosition, icon, active, children, ...linkProps } = {
        ...defaultProps,
        ...props
    } as HybridLinkButtonProps;
    const tipOverflow = getTipOverflow(props);

    return (
        <HybridLink
            {...linkProps}
            className={getClassNameFromProps(props)}
            disabled={disabled}
            setRef={ref}
            useBaseStyles={false}
            data-tip-overflow={tipOverflow}
        >
            <ButtonContent {...props} />
        </HybridLink>
    );
});

HybridLinkButton.displayName = 'HybridLinkButton';

Button.displayName = 'Button';

export default Button;
